import {
  type CommunicationType,
  type DownloadCommunicationsResult,
  buildCommunicationFileNameBase,
  isNilOrEmpty,
  ONE_DAY_IN_SECONDS,
} from '@rentguru/commons-utils';
import { toast } from '@up2rent/ui-new';
import isNil from 'lodash/isNil';
import type { IntlFormatters } from 'react-intl';

import { fetchStorageObject, getUrl } from 'src/auth/storage';
import { triggerBlobDownload } from 'src/utils/blobDownloadUtils';

const triggerBrowserDownload = async (url: string, fileName: string) => {
  const response = await fetchStorageObject(url);
  triggerBlobDownload(await response.blob(), fileName);
};

export const triggerAnchorDownload = (url: string) => {
  const anchor = document.createElement('a');
  anchor.href = url;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

const downloadPdfFromS3Key = async (s3Key: string, fileName: string) => {
  const s3Object = await getUrl({
    key: `public/${s3Key}`,
    options: { expiresIn: ONE_DAY_IN_SECONDS },
  });
  await triggerBrowserDownload(s3Object.url.toString(), fileName);
};

interface CommunicationFileNameSource {
  type: CommunicationType;
  subject?: string | null;
  sentAt?: string | null;
  createdAt?: string | null;
}

export const buildCommunicationDownloadFileNameBase = (
  communication: CommunicationFileNameSource,
  formatMessage: IntlFormatters['formatMessage']
): string => {
  const label = isNilOrEmpty(communication.subject)
    ? formatMessage({ id: `enums.TemplateSubType.${communication.type}` })
    : (communication.subject ?? '');
  return buildCommunicationFileNameBase(communication, label);
};

interface DownloadResultHandlers {
  formatMessage: IntlFormatters['formatMessage'];
}

export const handleDownloadResult = async (
  result: DownloadCommunicationsResult | null,
  fileNameBase: string,
  { formatMessage }: DownloadResultHandlers
): Promise<boolean> => {
  if (!isNil(result?.key)) {
    await downloadPdfFromS3Key(result.key, `${fileNameBase}.pdf`);
    toast.success(formatMessage({ id: 'communications.download.ready' }));
    return true;
  }
  if (!isNil(result?.zipUrl)) {
    await triggerBrowserDownload(result.zipUrl, `${fileNameBase}.zip`);
    toast.success(formatMessage({ id: 'communications.download.ready' }));
    return true;
  }
  if (result?.success) {
    toast.info(formatMessage({ id: 'communications.download.preparing' }));
    return true;
  }
  toast.error(formatMessage({ id: 'communications.download.error' }));
  return false;
};
